/**
 * Lista de rotinas agendadas do tenant com ações de aprovação, rejeição,
 * desabilitação e acesso ao histórico de execuções.
 *
 * Regras de visibilidade de ações:
 * - Aprovar / Rejeitar: só em approval_status === 'pending'
 *   + Aprovar oculto se routine.created_by === currentUserId (aprovador ≠ criador)
 * - Desabilitar: só em approval_status === 'approved' && enabled === true
 * - Ver histórico: sempre disponível
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  Title,
  Button,
  Group,
  Table,
  Text,
  Badge,
  Alert,
  Center,
  Loader,
  ActionIcon,
  Tooltip,
} from '@mantine/core'
import {
  IconPlus,
  IconAlertCircle,
  IconInbox,
  IconCheck,
  IconX,
  IconPlayerStop,
  IconHistory,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useCurrentTenant, useAuthStore, useCan } from '@/store/auth'
import { routinesApi, type Routine } from '@/api/routines'
import { RoutineExecutions } from './RoutineExecutions'

// ─── Badge de status composto ────────────────────────────────────────────────

function RoutineStatusBadge({ routine }: { routine: Routine }) {
  if (routine.approval_status === 'pending') {
    return (
      <Badge color="yellow" variant="light" size="sm">
        Pendente
      </Badge>
    )
  }
  if (routine.approval_status === 'rejected') {
    return (
      <Badge color="red" variant="light" size="sm">
        Rejeitada
      </Badge>
    )
  }
  // approved
  if (routine.enabled) {
    return (
      <Badge color="green" variant="light" size="sm">
        Ativa
      </Badge>
    )
  }
  return (
    <Badge color="gray" variant="light" size="sm">
      Desabilitada
    </Badge>
  )
}

// ─── Badge de backend ────────────────────────────────────────────────────────

function BackendBadge({ backend }: { backend: 'sql' | 'shell' }) {
  return (
    <Badge color={backend === 'sql' ? 'blue' : 'orange'} variant="light" size="xs">
      {backend === 'sql' ? 'SQL' : 'Shell'}
    </Badge>
  )
}

// ─── Componente Principal ────────────────────────────────────────────────────

export function RoutinesList() {
  const navigate = useNavigate()
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const queryClient = useQueryClient()
  const can = useCan()
  const { user } = useAuthStore()
  const currentUserId = user?.id ?? null

  const [executionsRoutineId, setExecutionsRoutineId] = useState<number | null>(null)
  const [executionsRoutineName, setExecutionsRoutineName] = useState<string>('')

  // Permissão de aprovação (gate visual — backend também valida)
  const canApprove = can('routines', 'approve', tenantId)

  // ── Query ────────────────────────────────────────────────────────────────

  const { data, isLoading, error } = useQuery<Routine[]>({
    queryKey: ['routines', tenantId],
    queryFn: async () => {
      const res = await routinesApi.listRoutines(tenantId!)
      return res.data
    },
    enabled: !!tenantId,
  })

  const routines = data ?? []

  // ── Mutations ────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: (id: number) => routinesApi.approveRoutine(tenantId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routines', tenantId] })
      notifications.show({ color: 'green', message: 'Rotina aprovada com sucesso.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro ao aprovar rotina: ${err.message}` })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (id: number) => routinesApi.rejectRoutine(tenantId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routines', tenantId] })
      notifications.show({ color: 'orange', message: 'Rotina rejeitada.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro ao rejeitar rotina: ${err.message}` })
    },
  })

  const disableMutation = useMutation({
    mutationFn: (id: number) => routinesApi.disableRoutine(tenantId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routines', tenantId] })
      notifications.show({ color: 'blue', message: 'Rotina desabilitada.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro ao desabilitar rotina: ${err.message}` })
    },
  })

  // ── Handlers ─────────────────────────────────────────────────────────────

  function handleOpenExecutions(routine: Routine) {
    setExecutionsRoutineId(routine.id)
    setExecutionsRoutineName(routine.name)
  }

  function handleCloseExecutions() {
    setExecutionsRoutineId(null)
    setExecutionsRoutineName('')
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Stack gap="md">
      {/* Drawer de execuções */}
      {tenantId != null && executionsRoutineId != null && (
        <RoutineExecutions
          tenantId={tenantId}
          routineId={executionsRoutineId}
          routineName={executionsRoutineName}
          opened={executionsRoutineId !== null}
          onClose={handleCloseExecutions}
        />
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as rotinas. Tente novamente em instantes.
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Title order={3}>Rotinas Agendadas</Title>
        <Button
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={() => navigate('/rotinas/nova')}
        >
          Nova Rotina
        </Button>
      </Group>

      {isLoading && (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      )}

      {!isLoading && routines.length === 0 && !error && (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconInbox size={40} color="gray" />
            <Text c="dimmed" size="sm">
              Nenhuma rotina cadastrada para este tenant.
            </Text>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={12} />}
              onClick={() => navigate('/rotinas/nova')}
            >
              Criar primeira rotina
            </Button>
          </Stack>
        </Center>
      )}

      {!isLoading && routines.length > 0 && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              <Table.Th>Backend</Table.Th>
              <Table.Th>Agenda</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Ações</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {routines.map((routine) => {
              const isPending = routine.approval_status === 'pending'
              const isApprovedEnabled =
                routine.approval_status === 'approved' && routine.enabled
              // Aprovar oculto se o usuário logado é o criador da rotina
              const isCreator =
                currentUserId != null && routine.created_by === currentUserId
              const showApprove = isPending && canApprove && !isCreator
              const showReject = isPending
              const showDisable = isApprovedEnabled

              return (
                <Table.Tr key={routine.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {routine.name}
                    </Text>
                    {routine.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {routine.description}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <BackendBadge backend={routine.backend} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {routine.schedule_expr}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {routine.timezone}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <RoutineStatusBadge routine={routine} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {showApprove && (
                        <Tooltip label="Aprovar rotina">
                          <ActionIcon
                            variant="light"
                            color="green"
                            size="sm"
                            aria-label="Aprovar rotina"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate(routine.id)}
                          >
                            <IconCheck size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {showReject && (
                        <Tooltip label="Rejeitar rotina">
                          <ActionIcon
                            variant="light"
                            color="red"
                            size="sm"
                            aria-label="Rejeitar rotina"
                            loading={rejectMutation.isPending}
                            onClick={() => rejectMutation.mutate(routine.id)}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {showDisable && (
                        <Tooltip label="Desabilitar rotina">
                          <ActionIcon
                            variant="light"
                            color="orange"
                            size="sm"
                            aria-label="Desabilitar rotina"
                            loading={disableMutation.isPending}
                            onClick={() => disableMutation.mutate(routine.id)}
                          >
                            <IconPlayerStop size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Ver histórico de execuções">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="sm"
                          aria-label="Ver histórico de execuções"
                          onClick={() => handleOpenExecutions(routine)}
                        >
                          <IconHistory size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
